import { Placement } from 'shared/ui/types'

const OFFSET = 8 // Margin between trigger and tooltip
const VIEWPORT_PADDING = 16
const TOTAL_VIEWPORT_PADDING = 32 // 16px on each side

export const MAX_WIDTH = 300

export const calculateAvailableSpace = (triggerRect: DOMRect) => {
  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  return {
    top: triggerRect.top,
    bottom: viewportHeight - triggerRect.bottom,
    left: triggerRect.left,
    right: viewportWidth - triggerRect.right
  }
}

export const doesPlacementFit = (
  height: number,
  availableSpace: ReturnType<typeof calculateAvailableSpace>
): boolean => {
  return (
    availableSpace.top >= height &&
    availableSpace.bottom >= height &&
    availableSpace.left >= MAX_WIDTH &&
    availableSpace.right >= MAX_WIDTH
  )
}

export const findBestPlacement = (
  availableSpace: ReturnType<typeof calculateAvailableSpace>
): Placement => {
  const placements = [
    { placement: 'top' as const, space: availableSpace.top },
    { placement: 'bottom' as const, space: availableSpace.bottom },
    { placement: 'left' as const, space: availableSpace.left },
    { placement: 'right' as const, space: availableSpace.right }
  ]

  const bestBasePlacement = placements.reduce((best, current) =>
    current.space > best.space ? current : best
  ).placement

  // For now, return the base placement. The caller can decide to use a variant
  // if they prefer, but this function focuses on finding the placement with most space
  return bestBasePlacement
}

export const getBasePosition = (triggerRect: DOMRect) => {
  const scrollX = window.pageXOffset || document.documentElement.scrollLeft
  const scrollY = window.pageYOffset || document.documentElement.scrollTop

  return {
    scrollX,
    scrollY,
    baseTop: triggerRect.top + scrollY + triggerRect.height / 2,
    baseLeft: triggerRect.left + scrollX + triggerRect.width / 2
  }
}

interface CalculatePositionForPlacementProps {
  targetPlacement: Placement
  triggerRect: DOMRect
  basePosition: ReturnType<typeof getBasePosition>
  viewportWidth: number
  viewportHeight: number
  tooltipHeight: number
}

export const calculatePositionForPlacement = ({
  targetPlacement,
  triggerRect,
  basePosition,
  viewportWidth,
  viewportHeight,
  tooltipHeight
}: CalculatePositionForPlacementProps) => {
  const { scrollX, scrollY, baseTop, baseLeft } = basePosition
  const offset = OFFSET
  const tooltipMaxWidth = MAX_WIDTH

  let top: number
  let left: number
  let transform: string

  switch (targetPlacement) {
    case 'top': {
      top = triggerRect.top + scrollY - offset
      left = baseLeft
      transform = 'translateX(-50%) translateY(-100%)'

      // Check if tooltip would overflow top
      if (top - tooltipHeight < scrollY) {
        top = triggerRect.bottom + scrollY + offset
        transform = 'translateX(-50%)'
      }

      const tooltipWidth = Math.min(
        tooltipMaxWidth,
        viewportWidth - TOTAL_VIEWPORT_PADDING
      )
      const tooltipLeft = left - tooltipWidth / 2
      const tooltipRight = left + tooltipWidth / 2

      if (tooltipLeft < scrollX) {
        left = scrollX + tooltipWidth / 2 + VIEWPORT_PADDING
      } else if (tooltipRight > scrollX + viewportWidth) {
        left = scrollX + viewportWidth - tooltipWidth / 2 - VIEWPORT_PADDING
      }
      break
    }

    case 'bottom': {
      top = triggerRect.bottom + scrollY + offset
      left = baseLeft
      transform = 'translateX(-50%)'

      // Check if tooltip would overflow bottom
      if (top + tooltipHeight > scrollY + viewportHeight) {
        top = triggerRect.top + scrollY - offset
        transform = 'translateX(-50%) translateY(-100%)'
      }

      const tooltipWidth = Math.min(
        tooltipMaxWidth,
        viewportWidth - TOTAL_VIEWPORT_PADDING
      )
      const tooltipLeft = left - tooltipWidth / 2
      const tooltipRight = left + tooltipWidth / 2

      if (tooltipLeft < scrollX) {
        left = scrollX + tooltipWidth / 2 + VIEWPORT_PADDING
      } else if (tooltipRight > scrollX + viewportWidth) {
        left = scrollX + viewportWidth - tooltipWidth / 2 - VIEWPORT_PADDING
      }
      break
    }

    case 'left': {
      top = baseTop
      left = triggerRect.left + scrollX - offset
      transform = 'translateX(-100%) translateY(-50%)'

      // Check if tooltip would overflow left
      if (left - tooltipMaxWidth < scrollX) {
        left = triggerRect.right + scrollX + offset
        transform = 'translateY(-50%)'
      }

      const tooltipTop = top - tooltipHeight / 2
      const tooltipBottom = top + tooltipHeight / 2

      if (tooltipTop < scrollY) {
        top = scrollY + tooltipHeight / 2 + VIEWPORT_PADDING
      } else if (tooltipBottom > scrollY + viewportHeight) {
        top = scrollY + viewportHeight - tooltipHeight / 2 - VIEWPORT_PADDING
      }
      break
    }

    case 'right': {
      top = baseTop
      left = triggerRect.right + scrollX + offset
      transform = 'translateY(-50%)'

      // Check if tooltip would overflow right
      if (left + tooltipMaxWidth > scrollX + viewportWidth) {
        left = triggerRect.left + scrollX - offset
        transform = 'translateX(-100%) translateY(-50%)'
      }

      const tooltipTop = top - tooltipHeight / 2
      const tooltipBottom = top + tooltipHeight / 2

      if (tooltipTop < scrollY) {
        top = scrollY + tooltipHeight / 2 + VIEWPORT_PADDING
      } else if (tooltipBottom > scrollY + viewportHeight) {
        top = scrollY + viewportHeight - tooltipHeight / 2 - VIEWPORT_PADDING
      }
      break
    }

    case 'top-left': {
      top = triggerRect.top + scrollY - offset
      left = triggerRect.left + scrollX
      transform = 'translateY(-100%)'

      // Check if tooltip would overflow top, fallback to bottom-left
      if (top - tooltipHeight < scrollY) {
        top = triggerRect.bottom + scrollY + offset
        transform = ''
      }

      // Ensure tooltip doesn't overflow left viewport edge
      const tooltipWidth = Math.min(
        tooltipMaxWidth,
        viewportWidth - TOTAL_VIEWPORT_PADDING
      )
      const tooltipRight = left + tooltipWidth

      if (tooltipRight > scrollX + viewportWidth) {
        left = scrollX + viewportWidth - tooltipWidth - VIEWPORT_PADDING
      }
      break
    }

    case 'top-right': {
      top = triggerRect.top + scrollY - offset
      left = triggerRect.right + scrollX
      transform = 'translateX(-100%) translateY(-100%)'

      // Check if tooltip would overflow top, fallback to bottom-right
      if (top - tooltipHeight < scrollY) {
        top = triggerRect.bottom + scrollY + offset
        transform = 'translateX(-100%)'
      }

      // Ensure tooltip doesn't overflow right viewport edge
      const tooltipWidth = Math.min(
        tooltipMaxWidth,
        viewportWidth - TOTAL_VIEWPORT_PADDING
      )
      const tooltipLeft = left - tooltipWidth

      if (tooltipLeft < scrollX) {
        left = scrollX + tooltipWidth + VIEWPORT_PADDING
      }
      break
    }

    case 'bottom-left': {
      top = triggerRect.bottom + scrollY + offset
      left = triggerRect.left + scrollX
      transform = ''

      // Check if tooltip would overflow bottom, fallback to top-left
      if (top + tooltipHeight > scrollY + viewportHeight) {
        top = triggerRect.top + scrollY - offset
        transform = 'translateY(-100%)'
      }

      // Ensure tooltip doesn't overflow left viewport edge
      const tooltipWidth = Math.min(
        tooltipMaxWidth,
        viewportWidth - TOTAL_VIEWPORT_PADDING
      )
      const tooltipRight = left + tooltipWidth

      if (tooltipRight > scrollX + viewportWidth) {
        left = scrollX + viewportWidth - tooltipWidth - VIEWPORT_PADDING
      }
      break
    }

    case 'bottom-right': {
      top = triggerRect.bottom + scrollY + offset
      left = triggerRect.right + scrollX
      transform = 'translateX(-100%)'

      // Check if tooltip would overflow bottom, fallback to top-right
      if (top + tooltipHeight > scrollY + viewportHeight) {
        top = triggerRect.top + scrollY - offset
        transform = 'translateX(-100%) translateY(-100%)'
      }

      // Ensure tooltip doesn't overflow right viewport edge
      const tooltipWidth = Math.min(
        tooltipMaxWidth,
        viewportWidth - TOTAL_VIEWPORT_PADDING
      )
      const tooltipLeft = left - tooltipWidth

      if (tooltipLeft < scrollX) {
        left = scrollX + tooltipWidth + VIEWPORT_PADDING
      }
      break
    }

    default: {
      top = triggerRect.top + scrollY - offset
      left = baseLeft
      transform = 'translateX(-50%) translateY(-100%)'
      break
    }
  }

  return { top, left, transform }
}

export const getTriggerRect = (triggeredElement: HTMLElement | null) => {
  if (!triggeredElement) return new DOMRect()

  // If trigger has display: contents, use the first child element's dimensions

  const computedStyle = window.getComputedStyle(triggeredElement)

  if (
    computedStyle.display === 'contents' &&
    triggeredElement.children.length > 0
  ) {
    return triggeredElement.children[0].getBoundingClientRect()
  }

  return triggeredElement.getBoundingClientRect()
}
